import * as Yup from 'yup';
import { isAfter, isBefore, differenceInYears, subMonths } from 'date-fns';

import { paymentMethod, paymentType, policyType, offerExcessUM } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { canAddUmbrella, canAddUmbrellaUMUIM } from 'core/helpers/quoter.service';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import { FormAction } from 'core/store/offer-store';
import { connectedHomeSchema } from '../components/sidebar/components/discounts-card/connected-home-discount/connected-home-form/connected-home.validation-schema';
import {
  validUSState,
  requiredString,
  validMortgageDetails,
  validMortgageLoanNumber,
  validHomePurchaseDate,
  addressBlock
} from 'common/helpers/yup-helpers';
import homeSchema from './validation-schemas/home.validation-schema';
import autoSchema from './validation-schemas/auto.validation-schema';
import condoSchema from './validation-schemas/condo.validation-schema';
import farmSchema from './validation-schemas/farm.validation-schema';
import effectiveDateSchema from './validation-schemas/effective-date.validation-schema';
import { capitalize } from 'core/helpers/formatters';
import { rejectionBlocksOptionCheckout } from 'core/store/offer-store/offer-functions';
import { brandConstants } from 'brand-config';

Yup.addMethod(Yup.string, 'requiredString', requiredString);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'validMortgageDetails', validMortgageDetails);
Yup.addMethod(Yup.mixed, 'validMortgageLoanNumber', validMortgageLoanNumber);
Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);

const maxDate = new Date(new Date().setDate(new Date().getDate() + 59));

// Shared across the offer-update (offerValidationSchema) and checkout (checkoutValidationSchema)
// paths so the two can't drift. All three validate addresses with collapsed messaging (#30230):
// a fully-empty required address shows one summary row in the Validation Errors modal; once any
// subfield is set, per-subfield messages take over.

const interestedPartiesValidation = Yup.array()
  .of(
    Yup.object().shape({
      name: Yup.string().required('Interested party name or company is required'),
      relationship: Yup.string().required('Interested party relationship is required'),
      address: addressBlock('Interested party address').nullable()
    })
  )
  .nullable();

const requiredUnlessExcluded = (message) =>
  Yup.string()
    .nullable()
    .test('required-unless-excluded', message, function (value) {
      if (this.parent?.excludeDriver === true) {
        return true;
      }
      return value != null && String(value).trim() !== '';
    });

const driverNameAndDobSchema = Yup.object().shape({
  drivers: Yup.array()
    .of(
      Yup.object().shape({
        firstName: requiredUnlessExcluded('First name is required'),
        lastName: requiredUnlessExcluded('Last name is required'),
        dateOfBirth: requiredUnlessExcluded('Date of birth is required')
      })
    )
    .nullable()
});

// Renters coverage is an endorsement on the auto policy, so when present its location address
// is required. `.default(undefined).nullable()` short-circuits when there's no renters coverage
// (an auto offer without renters is not flagged); a present-but-empty location surfaces
// addressBlock's "Renters address is required" summary. Without `.default(undefined)`, a
// present-but-undefined value would still run the nested shape and wrongly flag renters-less offers.
const rentersCoverageValidation = Yup.object()
  .shape({ rentersLocation: addressBlock('Renters address') })
  .default(undefined)
  .nullable();

const offerValidationSchema = (session) =>
  Yup.object().shape({
    home: Yup.object()
      .nullable()
      .shape({
        purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate', false)
      }),
    condo: Yup.object()
      .nullable()
      .shape({
        purchaseDate: Yup.string().validHomePurchaseDate('condo.purchaseDate', false)
      }),
    isBix: Yup.boolean().nullable(),
    removedDrivers: Yup.array()
      .of(
        Yup.object().shape({
          removedReason: Yup.string()
            .required('Please select a reason for this person not being a driver on the policy.')
            .nullable()
        })
      )
      .nullable(),
    global: Yup.object()
      .shape({
        affinity: Yup.string().nullable(),
        autoBillingDayOfMonth: Yup.number().nullable(),
        autoPaymentMethod: Yup.string().when('$selectedOption', {
          is: (selectedOption) => selectedOption?.includes('A'),
          then: Yup.string().requiredString(),
          otherwise: Yup.string().nullable()
        }),
        autoPaymentType: Yup.string().when('$selectedOption', {
          is: (selectedOption) => selectedOption?.includes('A'),
          then: Yup.string()
            .requiredString()
            .test(
              'payInFullRequired',
              "Offer requires One Time Payment Frequency due to customer's prior canceled policy for non-payment",
              function (value) {
                if (session.isTeamLeader || !this.options.parent.priorCancelNonPay) {
                  return true;
                }
                // if paying in full, or if policy type is home, pass validation
                if (value === paymentType.OneTime || this.options.context?.selectedOption === policyType.Home) {
                  return true;
                }
                return false;
              }
            ),
          otherwise: Yup.string().nullable()
        }),
        billingDayOfMonth: Yup.number().nullable(),
        currentAutoCarrier: Yup.string().nullable(),
        currentHomeownersCarrier: Yup.string().nullable(),
        currentlyAutoInsured: Yup.boolean().nullable(),
        discountInventoryScore: Yup.boolean(),
        discountPaperless: Yup.boolean(),
        discountBixMutualAffinity: Yup.boolean().nullable(),
        homeBillingDayOfMonth: Yup.number().nullable(),
        homeownersPaymentMethod: Yup.string().when('$selectedOption', {
          is: (selectedOption) => selectedOption?.includes('H') || selectedOption?.includes('C'),
          then: Yup.string().requiredString(),
          otherwise: Yup.string().nullable()
        }),
        homeownersPaymentType: Yup.string().when('$selectedOption', {
          is: (selectedOption) => selectedOption?.includes('H') || selectedOption?.includes('C'),
          then: Yup.string()
            .requiredString()
            .test(
              'payInFullRequired',
              "Offer requires One Time Payment Frequency due to customer's prior canceled policy for non-payment",
              function (value) {
                if (
                  session.isTeamLeader ||
                  !this.options.parent.priorCancelNonPay ||
                  this.options.parent?.homeownersPaymentMethod === paymentMethod.Escrow
                ) {
                  return true;
                }
                // if paying in full, or if policy type is not a home policy
                if (value === paymentType.OneTime || !this.options.context?.selectedOption.includes('H')) {
                  return true;
                }
                return false;
              }
            ),
          otherwise: Yup.string().nullable()
        }),
        personalPropertyProtection: Yup.boolean().nullable(),
        farmBillingDayOfMonth: Yup.number().nullable(),
        farmPaymentMethod: Yup.string().when('$selectedOption', {
          is: (selectedOption) => isFarmBasedPolicyType(selectedOption),
          then: Yup.string().requiredString(),
          otherwise: Yup.string().nullable()
        }),
        farmPaymentType: Yup.string().when('$selectedOption', {
          is: (selectedOption) => isFarmBasedPolicyType(selectedOption),
          then: Yup.string().requiredString(),
          otherwise: Yup.string().nullable()
        }),
        directBillPreferredPaymentMethod: Yup.string().when(['$selectedOption', 'farmPaymentMethod'], {
          is: (selectedOption, farmPaymentMethod) =>
            isFarmBasedPolicyType(selectedOption) &&
            (farmPaymentMethod === paymentMethod.Direct || farmPaymentMethod === paymentMethod.BillToOther),
          then: Yup.string().requiredString(),
          // Monoline auto Direct / Bill to other (CFM, #36200) settles its first charge the same way.
          otherwise: Yup.string().when(['$selectedOption', 'autoPaymentMethod'], {
            is: (selectedOption, autoPaymentMethod) =>
              selectedOption === policyType.Auto &&
              (autoPaymentMethod === paymentMethod.Direct || autoPaymentMethod === paymentMethod.BillToOther),
            then: Yup.string().requiredString('First charge payment method is required'),
            otherwise: Yup.string().nullable()
          })
        })
      })
      .nullable(),
    includeUmbrella: Yup.boolean()
      .test(
        'canAddUmbrella',
        'Umbrella cannot be added because requirements are not met: the customer must have an active auto policy, auto BI must be at least $500K per occurrence ($250K/$500K for split or $500K for CSL), homeowner\u2019s liability must be at least $300K, and there cannot be any excluded drivers or drivers with a major violation, unverifiable driving record, or at-fault accident that disqualifies them from umbrella.',
        (val, { options: { context } }) => {
          if (val) {
            const {
              autoCoverage,
              homeCoverage,
              condoCoverage,
              drivers,
              global,
              selectedOption,
              linkedPolicyUmbrellaInputs
            } = context;
            const { policyLimitBIPD } = autoCoverage || {};
            // Select the coverage object by the offer's actual policy type: condo offers carry a
            // stale/default homeCoverage scaffold whose low coverageX wrongly rejected umbrella
            // (https://github.com/gobranch/branch/issues/31428) — and data-defaults stamp that same
            // scaffold (coverageX 100000) on EVERY offer, so on AB the offer's own homeCoverage
            // must be ignored entirely or `??` never falls through to the linked home policy.
            let ownPropertyCoverage = null;
            if (selectedOption?.includes(policyType.Condo)) {
              ownPropertyCoverage = condoCoverage;
            } else if (selectedOption?.includes(policyType.Home)) {
              ownPropertyCoverage = homeCoverage;
            }
            const { coverageX } = ownPropertyCoverage || {};

            // Bundle-to-existing offers (HB/AB) carry no autoCoverage (HB) or homeCoverage (AB)
            // of their own — those live on the linked customer's in-force policy, so fall back
            // to its values. Drivers are the union of both so an exclusion on either side
            // blocks umbrella. Fails closed when the linked policy hasn't loaded.
            return canAddUmbrella({
              policyLimitBIPD: policyLimitBIPD ?? linkedPolicyUmbrellaInputs?.policyLimitBIPD,
              coverageX: coverageX ?? linkedPolicyUmbrellaInputs?.coverageX,
              global,
              drivers: [...(drivers || []), ...(linkedPolicyUmbrellaInputs?.drivers || [])]
            });
          }
          return true;
        }
      )
      .nullable(),
    umbrellaCoverage: Yup.object()
      .when('includeUmbrella', {
        is: true,
        then: Yup.object().shape({
          liabilityCoverageLimit: Yup.number()
            .test('umbrella-limit-caped-at-2mil', 'You cannot increase coverage over $2m', (value) => {
              // currently not allowing umbrella higher than 2mil
              if (value > 2000000) {
                return false;
              }
              return true;
            })
            .required('Umbrella Limit is required')
            .nullable(),
          watercraftHullLengths: Yup.array()
            .of(
              Yup.number().test(
                'craftHullLengthsMax',
                'We do not allow boats with hull lengths over 26 feet.',
                (value, { options: { context } }) => {
                  const { includeUmbrella } = context;
                  return includeUmbrella ? value <= 26 : true;
                }
              )
            )
            .nullable(),
          uninsuredMotoristLimit: Yup.string()
            .test(
              'canHaveExcessUM',
              'You must have 500K per occurrence of UM/UIM to add excess UM to umbrella',
              (value, { options: { context } }) => {
                if (!value || value === '0') {
                  return true;
                }

                const { autoCoverage, state, includeUmbrella, linkedPolicyUmbrellaInputs } = context;
                // HB/AB offers have no autoCoverage of their own — read the UM/UIM limits from the
                // linked auto policy, like the canAddUmbrella test above
                const { policyLimitUMBI, policyLimitUIMBI } = autoCoverage || linkedPolicyUmbrellaInputs || {};

                const canAddUM = canAddUmbrellaUMUIM({ policyLimitUMBI, policyLimitUIMBI, state });

                return includeUmbrella ? canAddUM : true;
              }
            )
            .test(
              'stateCannotHaveExcessUM',
              'This state does not allow excess UM/UIM',
              (value, { options: { context } }) => {
                if (!value || value === '0') {
                  return true;
                }

                const { state, includeUmbrella } = context;

                return includeUmbrella ? offerExcessUM[state] : true;
              }
            )
            .test('requiredWhenOffering', 'Excess UM/UIM is required', (value, { options: { context } }) => {
              const { state } = context;
              return offerExcessUM[state] ? !!value : true;
            })
            .nullable(),
          numVehiclesTotal: Yup.number()
            .test(
              'minCount',
              'The total number of Umbrella vehicles cannot be less than the number of vehicles on the policy',
              function (val) {
                const { cars, autoCoverage, linkedPolicyUmbrellaInputs, includeUmbrella } = this.options.context || {};
                // Keyed on the offer having its own auto half: data-defaults stamp cars: [] on every
                // offer, so on HB/AB nullish/length checks never fall through to the linked auto's cars
                const numCars = ((autoCoverage ? cars : linkedPolicyUmbrellaInputs?.cars) || []).length;
                return includeUmbrella ? val >= numCars : true;
              }
            )
            .typeError('Number of vehicles is required')
            .nullable(),
          numLicensedDriversUnder25: Yup.number()
            .test(
              'minCountDrivers',
              'The total number of Umbrella drivers under age 25 cannot be less than the number of drivers under age 25 on the auto policy',
              function (val) {
                const { drivers, autoCoverage, linkedPolicyUmbrellaInputs, includeUmbrella } =
                  this.options.context || {};
                // The message says "on the auto policy": with no auto half on the offer (HB/AB), the
                // auto policy's drivers are the linked policy's. Keyed on autoCoverage, not
                // drivers.length — HB offers carry household applicants in drivers, and a union would
                // double-count the same person (different ids across offer and policy)
                const driversUnder25 = ((autoCoverage ? drivers : linkedPolicyUmbrellaInputs?.drivers) || []).filter(
                  (d) => {
                    // not relying on d.age here because that node is only accurate after save
                    // and we want to catch drivers under 25 in case they were added in the same update as adding umbrella
                    const age = differenceInYears(new Date(), new Date(d.dateOfBirth));
                    return age < 25;
                  }
                ).length;
                return includeUmbrella ? val >= driversUnder25 : true;
              }
            )
            .required('Number of Licensed Drivers Under 25 is required')
            .typeError('Number of licensed drivers under 25 is required'),
          numRVs: Yup.number()
            .required('Number of Golf Carts and Snowmobiles is required')
            .typeError('Number of Golf Carts and Snowmobiles is required'),
          numMotorcyclesScooters: Yup.number()
            .required('Number of Motorcycles/Scooters is required')
            .typeError('Number of Motorcycles/Scooters is required'),
          numATVs: Yup.number()
            .default(null)
            .required('Number of ATVs is required')
            .typeError('Number of ATVs is required'),
          numPersonalWatercraft: Yup.number()
            .required('Number of Personal Watercraft is required')
            .typeError('Number of Personal Watercraft is required')
        }),
        otherwise: Yup.object().nullable()
      })

      .nullable(),
    scheduledPersonalProperty: Yup.object()
      .shape({
        deductible: Yup.string()
          .test('sppDeductible', 'SPP deductible is required.', (value, { options }) => {
            if (options.context?.scheduledPersonalProperty?.items?.length) {
              return !!value;
            }
            return true;
          })
          .nullable(),
        items: Yup.array().nullable()
      })
      .nullable(),
    interestedParties: interestedPartiesValidation,
    rentersCoverage: rentersCoverageValidation
  });

const michiganPIPSchema = Yup.object().shape({
  auto: Yup.object().shape({
    pipEligibilityConfirmation: Yup.boolean()
      .nullable()
      .test(
        'PIP.Coverage.Acknowledgment',
        'Please acknowledge that the policy meets the criteria for the selected PIP limit.',
        (val, { options: { context } }) => {
          const { autoCoverage, state } = context;

          if (
            state === 'MI' &&
            ['50000', 'OPTOUT', 'NONE', '250000W/EXCLUDED'].includes(autoCoverage?.policyLimitPIPME)
          ) {
            return !!val;
          }
          return true;
        }
      )
  })
});

const michiganPIPConfirmCheckoutSchema = Yup.object().shape({
  auto: Yup.object().shape({
    pipResidentsConfirmation: Yup.boolean().test(
      'PIP.Household.Residents.Acknowledgement',
      'Please acknowledge that you’ve included all household residents for PIP.',
      (value) => value
    )
  })
});

// this schema is to support the modify offer page now that we can navigate there with rejections.
// this will prevent us from moving to checkout with a rejected option
const rejectionsSchema = (rejections, selectedOption) =>
  Yup.object().shape({
    oneOrMorePoliciesOnThisOfferCannotBePurchased: Yup.mixed().test(
      'noRejectedTypes',
      `This offer has characteristics that do not meet ${brandConstants.DISPLAY_NAME}'s underwriting guidelines. See the offer rejections section for more information on potential next steps.`,
      () => {
        if (!rejections.length) {
          return true;
        }
        // A soft rejection on a farm-based option (F/Z/Y) must NOT block here — the agent needs to
        // reach the UW-review form to resolve it, and farm checkout is gated on UW approval separately
        // (#33355). Hard rejections and all home/auto rejections still block. See
        // rejectionBlocksOptionCheckout.
        return !rejections?.some((rejection) => rejectionBlocksOptionCheckout(rejection, selectedOption));
      }
    )
  });

// Checkout = the full update schema PLUS the checkout-only additions below (#32538). It used to
// be an independent copy of the update schema, which silently drifted: four bind-critical rules
// (SPP deductible, the umbrella block, removedDrivers reason, prior-cancel pay-in-full) existed
// only on the update path and vanished at the one moment that's supposed to be the enforcement
// point. Layering via concat makes every update rule hold at checkout by construction, so the
// additions shape must only contain checkout-specific rules — anything shared belongs in
// offerValidationSchema.
const checkoutValidationSchema = (session, state) =>
  offerValidationSchema(session).concat(checkoutAdditionsSchema(state));

const checkoutAdditionsSchema = (state) =>
  Yup.object().shape({
    home: Yup.object()
      .nullable()
      .shape({
        purchaseAmount: Yup.number()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .nullable()
          .test(
            'required-when-new-purchase',
            'Purchase amount is required when purchase date is within the last 6 months',
            function (value) {
              if (['MI', 'MD', 'PA'].includes(state)) {
                return true;
              }

              const { purchaseDate } = this.parent;
              if (!purchaseDate) {
                return true;
              }

              const sixMonthsAgo = subMonths(new Date(), 6);

              if (isAfter(new Date(purchaseDate), sixMonthsAgo)) {
                return value;
              }

              return true;
            }
          )
      }),
    forcedMultiPolicyDiscount: Yup.string()
      .test(
        'existingCustomerCheck',
        'You must have an existing member attached to add a multilineDiscount',
        function (value) {
          if (value && value !== 'N' && !this.options.context.existingCustomer?.id) {
            return false;
          }
          return true;
        }
      )
      .nullable(),
    global: Yup.object().shape({
      autoEffectiveDate: Yup.date().nullable(),
      homeEffectiveDate: Yup.date()
        .nullable()
        .test('min', 'Policy start date cannot be in the past or before the home purchase date', function (obj) {
          if (this.options.context?.selectedOption?.includes('H')) {
            const purchaseDate = this.options.context?.home?.purchaseDate;
            const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
            const now = new Date();
            const minDateHome =
              purchaseDateToCompare && isAfter(purchaseDateToCompare, now)
                ? purchaseDateToCompare.setDate(purchaseDateToCompare.getDate() - 1)
                : now.setDate(now.getDate() - 1);
            const date = new Date(obj);
            return isBefore(minDateHome, date);
          }
          return true;
        })

        .test('max', 'Policy start date must be within the next 60 days', function (obj) {
          if (this.options.context?.selectedOption?.includes('H')) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        }),
      condoEffectiveDate: Yup.date()
        .nullable()
        .test('min', 'Policy start date cannot be in the past or before the condo purchase date', function (obj) {
          if (this.options.context?.selectedOption?.includes('C')) {
            const purchaseDate = this.options.context?.condo?.purchaseDate;
            const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
            const now = new Date();
            const minDateCondo =
              purchaseDateToCompare && isAfter(purchaseDateToCompare, now)
                ? purchaseDateToCompare.setDate(purchaseDateToCompare.getDate() - 1)
                : now.setDate(now.getDate() - 1);
            const date = new Date(obj);
            return isBefore(minDateCondo, date);
          }
          return true;
        })

        .test('max', 'Policy start date must be within the next 60 days', function (obj) {
          if (this.options.context?.selectedOption?.includes('C')) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        })
    }),
    // Mortgage-lender details are required only when the escrowed policy is actually being checked
    // out. Each policy type carries its own payment method on `global` (each defaulting to Escrow
    // 'W' even when that policy isn't on the offer), so pair the selected policy type with its own
    // payment method: Home→homeownersPaymentMethod, Condo→condoPaymentMethod, Farm→farmPaymentMethod.
    primaryMortgageDetail: Yup.object()
      .when(
        ['$selectedOption', 'global.homeownersPaymentMethod', 'global.condoPaymentMethod', 'global.farmPaymentMethod'],
        {
          is: (selectedOption, homeownersPaymentMethod, condoPaymentMethod, farmPaymentMethod) =>
            (selectedOption?.includes(policyType.Home) && homeownersPaymentMethod === paymentMethod.Escrow) ||
            (selectedOption?.includes(policyType.Condo) && condoPaymentMethod === paymentMethod.Escrow) ||
            (isFarmBasedPolicyType(selectedOption) && farmPaymentMethod === paymentMethod.Escrow),
          then: Yup.object().shape({
            mortgageHolderName: Yup.string().validMortgageDetails('mortgageHolderName'),
            loanNumber: Yup.string()
              .validMortgageDetails('loanNumber')
              .typeError('Loan number is required')
              .validMortgageLoanNumber(),
            // This branch only runs when the selected home/condo/farm is escrowed, so the lender
            // address is unconditionally required here — addressBlock collapses a fully-empty address
            // to a single "Mortgage lender address is required" row, matching renters/lienholder.
            mortgageHolderAddress: addressBlock('Mortgage lender address').typeError(
              'Mortgage lender address must be fully filled in'
            )
          }),
          otherwise: Yup.object().nullable()
        }
      )
      .nullable(),
    drivers: Yup.array()
      .of(
        Yup.object().shape({
          driversLicenseNumber: Yup.string()
            .test('drivers-license-number-required', function (val) {
              const selectedOption = this.options.context.selectedOption;
              const formattedName = `${capitalize(this.options?.parent?.firstName)} ${capitalize(this.options?.parent?.lastName)}`;
              const skipLicenseNumValidation = this.options?.parent?.skipLicenseNumValidation;

              // Skip validation if flag is set
              if (skipLicenseNumValidation) {
                return true;
              }

              // Only require driver's license for Auto policies
              if (!selectedOption?.includes(policyType.Auto)) {
                return true;
              }

              // For Auto policies, require driver's license
              if (!val) {
                return this.createError({
                  message: `${formattedName} requires a driver's license number.`
                });
              }
              return true;
            })
            .concat(driversLicenseNumberSchema)
            .nullable()
        })
      )
      .nullable()
  });

export const buildSchema = async ({
  formAction,
  includeConnectedHome,
  isAdvancedConnectedHome,
  needMVRs,
  session,
  selectedOption = '',
  state,
  rejections = []
}) => {
  let schema;

  if ([FormAction.Checkout, FormAction.PreBindUWValidation].includes(formAction) && !needMVRs) {
    schema = checkoutValidationSchema(session, state);
    if (rejections.length > 0) {
      schema = schema.concat(rejectionsSchema(rejections, selectedOption));
    }

    if (selectedOption.includes(policyType.Auto) && state === 'MI') {
      schema = schema.concat(michiganPIPConfirmCheckoutSchema);
    }
  } else {
    schema = offerValidationSchema(session);
  }

  // Skipped for the expired-offer date refresh, which submits without user edits (see the
  // effectiveDateSchema note below).
  if (formAction !== FormAction.UpdateEffectiveDates) {
    schema = schema.concat(driverNameAndDobSchema);
  }

  if (selectedOption.includes(policyType.Auto) && state === 'MI') {
    schema = schema.concat(michiganPIPSchema);
  }

  if (selectedOption.includes(policyType.Home)) {
    schema = schema.concat(homeSchema(state));
  }

  if (selectedOption.includes(policyType.Condo)) {
    schema = schema.concat(condoSchema);
  }

  if (selectedOption.includes(policyType.Auto)) {
    schema = schema.concat(autoSchema(state, session));
  }

  if (isFarmBasedPolicyType(selectedOption)) {
    schema = schema.concat(await farmSchema());
  }

  if (includeConnectedHome) {
    schema = schema.concat(connectedHomeSchema(isAdvancedConnectedHome, session.isAgency));
  }
  // If the offer is out of date (meaning the effective dates are in the past 30+ days)
  // we don't want the validation to enforce the effective date rules, since the form's submit function
  // will automatically update the effective dates to today
  // This is because the validation will stop the form from being submitted if there are validation errors
  if (formAction !== FormAction.UpdateEffectiveDates) {
    schema = schema.concat(effectiveDateSchema);
  }
  return schema;
};
